import type { AppProps } from 'next/app';

import {
  ApolloProvider,
  ErrorResponse,
  useApollo,
} from '@collinsonx/utils/apolloBooking';
import { UserProvider } from '@collinsonx/utils/lib/userContext';
import { SuperTokensWrapper } from '@collinsonx/utils/supertokens';
import GlobalErrorBoundary from '@components/GlobalErrorBoundary';
import MantineWrapper from '@components/MantineWrapper';
import Orchestrator from '@components/Orchestrator/Orchestrator';
import PageTransition from '@components/PageTransition/PageTransition';
import { useLogStorage } from '@lib/logging';
import { useMaintenanceMode } from 'config/features';
import { isMaintenancePage, redirectTo, ROUTES } from 'config/navigation';
import BookingProvider from 'context/BookingContext';
import DomainProvider from 'context/DomainContext';
import ProductProvider from 'context/ProductContext';
import { PayloadProvider } from 'hooks/payload';
import { NextPage } from 'next';
import { useEffect, useRef, useState } from 'react';
import { initDatadogMonitoring } from 'utils/datadog';
import Observable from 'utils/observer';
import { initSuperTokens } from 'utils/supertokens';

import '../globalStyles.css';

type Props = {
  Component: NextPage;
} & AppProps;

initSuperTokens();
initDatadogMonitoring();

export default function MyApp({ Component, pageProps }: Props) {
  const apolloErrorObservable = useRef(new Observable<ErrorResponse>());
  const apolloClient = useApollo(pageProps, apolloErrorObservable.current);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const { setDeviceAndSessionIDsData } = useLogStorage();

  useEffect(() => {
    setIsHydrated(true);
    setTimeout(() => setIsLayoutReady(true), 300);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    setDeviceAndSessionIDsData();
  }, [isHydrated]);

  useEffect(() => {
    if (useMaintenanceMode && !isMaintenancePage())
      redirectTo(ROUTES.MAINENANCE);
  }, [useMaintenanceMode]);

  return (
    <>
      <PageTransition isLayoutReady={isLayoutReady} />
      {!isHydrated ? (
        <PageTransition isLayoutReady={false} />
      ) : (
        <DomainProvider>
          <UserProvider>
            <SuperTokensWrapper>
              <ApolloProvider client={apolloClient}>
                <GlobalErrorBoundary>
                  <PayloadProvider>
                    <MantineWrapper>
                      <ProductProvider>
                        <BookingProvider>
                          <Orchestrator>
                            <Component {...pageProps} />
                          </Orchestrator>
                        </BookingProvider>
                      </ProductProvider>
                    </MantineWrapper>
                  </PayloadProvider>
                </GlobalErrorBoundary>
              </ApolloProvider>
            </SuperTokensWrapper>
          </UserProvider>
        </DomainProvider>
      )}
    </>
  );
}
